import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '../api/client.js';
import clsx from 'clsx';

const TABS = [
  { id: 'aging',    label: 'Aging fleet' },
  { id: 'top',      label: 'Top customers' },
  { id: 'pipeline', label: 'Pipeline' },
];

function fmt$(v) {
  const n = Number(v || 0);
  if (n === 0) return '$0';
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + Math.round(n).toLocaleString();
  return '$' + n.toFixed(0);
}

export default function TrailerSales() {
  const [tab, setTab] = useState('aging');

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Trailer Sales</div>
        <h1 className="text-2xl">Trailer Sales</h1>
        <div className="rule-crimson" />
      </header>

      <div className="border-b border-panelDark flex gap-1">
        {TABS.map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-2 text-sm border-b-2 -mb-px',
              tab === t.id ? 'border-crimson text-crimson font-medium' : 'border-transparent text-navy hover:text-crimson'
            )}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'top'      && <TopCustomersTab />}
      {tab === 'aging'    && <AgingFleetTab />}
      {tab === 'pipeline' && <PipelineTab />}
    </div>
  );
}

function TopCustomersTab() {
  const [branchId, setBranchId] = useState('');

  const q = useQuery({
    queryKey: ['trailer-sales', 'top-customers', branchId],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('limit', '50');
      if (branchId) p.set('branch_id', branchId);
      return api.get(`/trailer-sales/top-customers?${p.toString()}`).then(r => r.data);
    },
    keepPreviousData: true,
  });
  const rows = q.data?.top_customers || [];
  if (q.isError) return <div className="text-crimson text-sm">Failed to load top customers.</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Customers ranked by trailing 12 months of trailer sales revenue.
      </p>

      <div className="bg-panel rounded p-2 flex flex-wrap items-center gap-3 text-sm">
        <select value={branchId} onChange={e => setBranchId(e.target.value)}
                className="px-2 py-1 rounded border border-panelDark bg-white">
          <option value="">All branches</option>
          <option value="3">Fontana</option>
          <option value="2">Fresno</option>
          <option value="1">Lathrop</option>
          <option value="4">Delhi</option>
          <option value="5">Troutdale</option>
        </select>
        <div className="ml-auto text-xs text-muted">{q.isLoading ? 'Loading…' : `${rows.length} customers`}</div>
      </div>

      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">#</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-right px-3 py-2">Trailer sales (12mo)</th>
              <th className="text-right px-3 py-2">Last sale</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.customer_id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5 text-muted tabular-nums">{i + 1}</td>
                <td className="px-3 py-1.5">
                  <Link to={`/customers/${r.customer_id}`} className="text-navy hover:text-crimson">{r.name}</Link>
                  {Number(r.row_count) > 1 && (
                    <span className="ml-2 text-[10px] text-muted">({r.row_count} rows merged)</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium">{fmt$(r.trailer_12mo)}</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{r.last_period?.slice(0, 7) || '—'}</td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={4} className="text-center text-muted py-6">No trailer sales in the last 12 months.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PipelineTab() {
  const q = useQuery({
    queryKey: ['trailer-sales', 'pipeline'],
    queryFn: () => api.get('/trailer-sales/pipeline').then(r => r.data),
  });
  const rows = q.data?.data || [];

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted">
        Open opportunities tagged Trailer Sales. Set an opportunity's <em>domain</em> to <code>trailer_sales</code> to appear here.
      </p>
      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Stage</th>
              <th className="text-right px-3 py-2">Value</th>
              <th className="text-right px-3 py-2">Activity</th>
              <th className="text-right px-3 py-2">Close by</th>
              <th className="text-left px-3 py-2">Owner</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(o => (
              <tr key={o.id} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  <Link to={`/pipeline/${o.id}`} className="text-navy hover:text-crimson font-medium">{o.name}</Link>
                </td>
                <td className="px-3 py-1.5">
                  {o.customer_id
                    ? <Link to={`/customers/${o.customer_id}`} className="text-navy hover:text-crimson">{o.customer_name}</Link>
                    : '—'}
                </td>
                <td className="px-3 py-1.5 capitalize">{o.stage}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(o.value_usd)}</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">
                  {o.source_note_date ? new Date(o.source_note_date).toLocaleDateString() : '—'}
                </td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{o.expected_close_date || '—'}</td>
                <td className="px-3 py-1.5 text-xs text-muted">{o.owner_name || '—'}</td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted py-6">No trailer sales opportunities yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgingFleetTab() {
  const [oldYears, setOldYears] = useState(10);
  const [minFleet, setMinFleet] = useState(5);
  const [minOld, setMinOld] = useState(3);
  const [branchId, setBranchId] = useState('');

  const q = useQuery({
    queryKey: ['trailer-sales', 'aging-fleet', oldYears, minFleet, minOld, branchId],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('old_threshold_years', oldYears);
      p.set('min_fleet', minFleet);
      p.set('min_old_count', minOld);
      p.set('limit', '100');
      if (branchId) p.set('branch_id', branchId);
      return api.get(`/trailer-sales/aging-fleet?${p.toString()}`).then(r => r.data);
    },
    keepPreviousData: true,
  });
  const rows = q.data?.data || [];
  if (q.isError) return <div className="text-crimson text-sm">Failed to load aging fleet data.</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Customers whose fleet skews old — ripe for replacement conversations. Sorted by count of trailers older than the threshold.
      </p>

      <div className="bg-panel rounded p-2 flex flex-wrap items-center gap-3 text-sm">
        <Field label="Threshold" suffix="years old">
          <input type="number" min="3" max="30" value={oldYears}
            onChange={e => setOldYears(parseInt(e.target.value, 10) || 10)}
            className="w-16 px-2 py-1 rounded border border-panelDark bg-white" />
        </Field>
        <Field label="Min fleet">
          <input type="number" min="1" max="500" value={minFleet}
            onChange={e => setMinFleet(parseInt(e.target.value, 10) || 5)}
            className="w-16 px-2 py-1 rounded border border-panelDark bg-white" />
        </Field>
        <Field label="Min old">
          <input type="number" min="1" max="100" value={minOld}
            onChange={e => setMinOld(parseInt(e.target.value, 10) || 3)}
            className="w-16 px-2 py-1 rounded border border-panelDark bg-white" />
        </Field>
        <select value={branchId} onChange={e => setBranchId(e.target.value)}
                className="px-2 py-1 rounded border border-panelDark bg-white">
          <option value="">All branches</option>
          <option value="202">Fontana</option>
          <option value="203">Fresno</option>
          <option value="204">Lathrop</option>
          <option value="205">Delhi</option>
          <option value="301">Troutdale</option>
        </select>
        <div className="ml-auto text-xs text-muted">{q.isLoading ? 'Loading…' : `${rows.length} customers`}</div>
      </div>

      <div className="bg-white border border-panelDark rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-panel text-navy uppercase text-[10px] tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Branch</th>
              <th className="text-right px-3 py-2">Fleet</th>
              <th className="text-right px-3 py-2">Avg yr</th>
              <th className="text-right px-3 py-2">Oldest</th>
              <th className="text-right px-3 py-2">≥{oldYears}y</th>
              <th className="text-right px-3 py-2">% old</th>
              <th className="text-right px-3 py-2">Last service</th>
              <th className="text-right px-3 py-2">Lifetime $</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={`${r.cus_id}-${r.excede_branch_id}`} className="border-t border-panel hover:bg-panel/60">
                <td className="px-3 py-1.5">
                  {r.customer_id
                    ? <Link to={`/customers/${r.customer_id}`} className="text-navy hover:text-crimson">{r.cus_name}</Link>
                    : <span>{r.cus_name}</span>}
                  <span className="text-[10px] text-muted ml-2 font-mono">{r.cus_id}</span>
                </td>
                <td className="px-3 py-1.5 text-xs text-muted">{r.branch_name || `br ${r.excede_branch_id}`}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.fleet_size}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.avg_year}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-muted">{r.oldest_year}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium">{r.over_threshold}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{r.pct_over_threshold}%</td>
                <td className="px-3 py-1.5 text-right text-xs text-muted">{r.last_service_at?.slice(0,10) || '—'}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{fmt$(r.lifetime_spend)}</td>
              </tr>
            ))}
            {!q.isLoading && rows.length === 0 && (
              <tr><td colSpan={9} className="text-center text-muted py-6">No customer fleets match these thresholds.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, small }) {
  return (
    <div className={clsx('border bg-panel border-panelDark rounded px-3', small ? 'py-1.5' : 'py-2')}>
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className={clsx(small ? 'text-lg' : 'text-2xl', 'font-semibold tabular-nums')}>{value}</div>
    </div>
  );
}
function Field({ label, suffix, children }) {
  return (
    <label className="flex items-center gap-1 text-xs text-navy">
      {label} {children}
      {suffix && <span className="text-muted">{suffix}</span>}
    </label>
  );
}
